import React, { useEffect, useRef, useState } from 'react';

import { Icon } from '@/components/shared/element/icons';
import { Link } from '@/components/shared/element/link';
import { Text } from '@/components/shared/element/text';
import { Box } from '@/components/shared/layout/box';
import { removeSpan } from '@/components/shared/utility/format';
import { useWindowSize } from '@/components/shared/utility/hooks';
import {
  clearBodyLockScroll,
  enableBodyLockScroll,
} from '@/components/shared/utility/lock-scroll';
import { styled } from '@/stitches.config';
import { IMenu } from '@/types/layout/menu';

const FloatingMenu = styled('div', {
  position: 'fixed',
  right: 16,
  bottom: 24,
  zIndex: 1002,
  fontSize: 0,
  '@xs': {
    right: 24,
    bottom: 24,
  },
  '@lg': {
    right: 32,
  },
  variants: {
    open: {
      true: {
        zIndex: 1005,
      },
    },
  },
});

const FloatingMenuPanel = styled(Box, {
  width: 'max-content',
  right: 0,
  bottom: 48 + 12,
  bc: '$white',
  br: '$rounded-2',
  bs: '0px 8px 16px rgba(0, 0, 0, 0.16)',
  opacity: 0,
  pe: 'none',
  transform: 'translateY(16px)',
  transition:
    'opacity 0.5s cubic-bezier(0.5, 0, 0, 0.75), transform 0.5s cubic-bezier(0.5, 0, 0, 0.75)',
  '@lg': {
    bottom: 56 + 12,
  },
  variants: {
    open: {
      true: {
        opacity: 1,
        pe: 'all',
        transform: 'translateY(0)',
      },
    },
  },
});

const FloatingMenuPanelInner = styled('div', {
  display: 'inline-flex',
  flexDirection: 'column',
  rowGap: 4,
  p: 16,
});

const FloatingMenuLink = styled(Link, {
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  columnGap: 12,
  px: 16,
  py: 12,
  color: '$gray900',
  us: 'none',
  height: 48,
  '&:before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    bc: '$gray300',
    br: '$rounded-1',
    pe: 'none',
    opacity: 0,
    transition: 'opacity 0.3s ease',
  },
  '&:hover': {
    '&:before': {
      opacity: 1,
    },
  },
});

const FloatingMenuOverlay = styled('div', {
  '@maxsm': {
    position: 'fixed',
    width: '120vw',
    height: '120vh',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    bc: '$backdrop70',
    backdropFilter: 'blur(4px)',
    zIndex: '-1',
    '-webkit-overflow-scrolling': 'none',
    opacity: 0,
    pe: 'none',
    transition: 'opacity 0.5s cubic-bezier(0.5, 0, 0, 0.75)',
  },
  '@sm': {
    display: 'none',
  },
  variants: {
    open: {
      true: {
        opacity: 1,
        pe: 'all',
        '@sm': {
          opacity: 0,
          pe: 'none',
        },
      },
    },
  },
});

const FloatingMenuBar = styled('div', {
  position: 'absolute',
  right: 0,
  display: 'block',
  height: 2,
  width: 20,
  bc: '$white',
  transformOrigin: 'right center',
  // transition: 'transform 0.5s cubic-bezier(0.5, 0, 0, 0.75)',
  transition: 'transform 0.45s cubic-bezier(0.475, 0.425, 0, 0.995) 0s',
  '&:nth-child(1)': {
    top: 0,
    transform: `scaleX(${16 / 20})`,
  },
  '&:nth-child(2)': {
    top: 6,
  },
  '&:nth-child(3)': {
    top: 12,
    transform: `scaleX(${12 / 20})`,
  },
});

const FloatingMenuIcon = styled('div', {
  position: 'relative',
  fontSize: 0,
  height: 14,
  width: 18,
  '@lg': {
    width: 20,
  },
  variants: {
    open: {
      true: {
        [`& ${FloatingMenuBar}`]: {
          bc: '$gray900',
          transformOrigin: 'center center',
          '&:nth-child(1)': {
            top: 0,
            transform: 'scaleX(1) translateY(6px) rotate(-45deg)',
          },
          '&:nth-child(2)': {
            top: 6,
            width: 0,
            // transform: 'scaleX(0)',
          },
          '&:nth-child(3)': {
            top: 12,
            transform: 'scaleX(1) translateY(-6px) rotate(45deg)',
          },
        },
      },
    },
  },
});

const FloatingMenuButton = styled('button', {
  appearance: 'none',
  border: 'none',
  outline: 'none',
  cursor: 'pointer',
  width: 48,
  height: 40,
  position: 'absolute',
  right: 0,
  bottom: 0,
  br: '$rounded-1',
  bc: '$gray800',
  bs: '0px 1px 4px rgba(0, 0, 0, 0.16)',
  zIndex: 1,
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  opacity: 0,
  pe: 'none',
  transform: 'translateX(100px)',
  transition:
    'background-color 0.5s cubic-bezier(0.5, 0, 0, 0.75), opacity 0.5s cubic-bezier(0.5, 0, 0, 0.75), transform 0.5s cubic-bezier(0.5, 0, 0, 0.75)',
  '@lg': {
    width: 56,
    height: 48,
  },
  '& *': {
    pe: 'none',
  },
  '@hover': {
    '&:hover': {
      bc: '$gray700',
      [`& ${FloatingMenuBar}`]: {
        transform: 'scaleX(1)',
      },
    },
  },
  variants: {
    show: {
      true: {
        opacity: 1,
        pe: 'all',
        transform: 'translateX(0)',
      },
    },
    open: {
      true: {
        bc: '$white !important',
      },
    },
  },
});

export interface IMY24FloatingBuyingTools {
  menu?: IMenu[];
}

const View = (props: IMY24FloatingBuyingTools) => {
  const FLOATING_REF = useRef(null);
  const [togglePanel, setTogglePanel] = useState(false);
  const [visible, setVisible] = useState(false);
  const [show, setShow] = useState(false);
  const WINDOW_SIZE = useWindowSize();

  useEffect(() => {
    const toggleVisibility = () => {
      if (window.scrollY > 200) {
        setVisible(true);
      }

      if (window.scrollY > 500) {
        setShow(true);
      } else {
        setShow(false);
        setTogglePanel(false);
      }
    };

    window.addEventListener('scroll', toggleVisibility);

    return () => window.removeEventListener('scroll', toggleVisibility);
  }, []);

  const disableScrolling = (e) => {
    if (e.cancelable) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  useEffect(() => {
    if (!FLOATING_REF.current) return;

    if (WINDOW_SIZE.width < 640) {
      if (togglePanel) {
        enableBodyLockScroll();
        FLOATING_REF.current.addEventListener('pointermove', disableScrolling, {
          passive: false,
        });
        FLOATING_REF.current.addEventListener('touchmove', disableScrolling, {
          passive: false,
        });
      } else {
        clearBodyLockScroll();
      }
    }

    return () => {
      if (!FLOATING_REF.current) return;
      FLOATING_REF.current.removeEventListener(
        'pointermove',
        disableScrolling,
        {
          passive: false,
        }
      );
      // eslint-disable-next-line react-hooks/exhaustive-deps
      FLOATING_REF.current.removeEventListener('touchmove', disableScrolling, {
        passive: false,
      });
    };
  }, [togglePanel, WINDOW_SIZE.width]);

  if (!visible) return null;

  return (
    <FloatingMenu
      ref={FLOATING_REF}
      open={togglePanel}
      data-test="floatingMenu"
    >
      <FloatingMenuButton
        show={show}
        open={togglePanel}
        onClick={() => setTogglePanel(!togglePanel)}
        data-test="floatingMenu_button"
        aria-label="Menu"
      >
        <FloatingMenuIcon open={togglePanel}>
          <FloatingMenuBar />
          <FloatingMenuBar />
          <FloatingMenuBar />
        </FloatingMenuIcon>
      </FloatingMenuButton>
      <FloatingMenuPanel
        position="absolute"
        open={togglePanel}
        test={{
          dataTest: 'floatingMenu_lists',
        }}
      >
        <FloatingMenuPanelInner>
          {props.menu?.map((o, i) => (
            <FloatingMenuLink
              key={i}
              href={o?.url}
              target={o?.target}
              tracking={{
                dataTrack: 'floating-menu',
                dataTrackUrl: o?.url,
                dataTrackText: removeSpan(o?.label),
              }}
              test={{
                dataTest: 'floatingMenu_link',
              }}
            >
              {!!o?.icon && <Icon color="icon-red" icon={o?.icon} />}
              <Text
                as="p"
                size="sm"
                font="bold"
                variant="text-gray-darkest"
                wrap
                dangerouslySetInnerHTML={{ __html: o?.label }}
                data-test="floatingMenu_title"
              />
            </FloatingMenuLink>
          ))}
        </FloatingMenuPanelInner>
      </FloatingMenuPanel>
      <FloatingMenuOverlay
        open={togglePanel}
        onClick={() => setTogglePanel(!togglePanel)}
        data-test="floatingMenu_overlay"
      />
    </FloatingMenu>
  );
};
View.displayName = 'SectionMY24SharedFloatingBuyingTools';
export default View;
